import { ReadonlyMat3, mat3, vec3 } from 'gl-matrix';

/**
 * Anatomical direction label in DICOM LPS convention.
 */
export type DirectionLabel = 'L' | 'R' | 'A' | 'P' | 'H' | 'F';

/**
 * Maps a patient-space direction vector to an anatomical label.
 * Uses DICOM LPS convention:
 *   +X = Left (L), -X = Right (R)
 *   +Y = Posterior (P), -Y = Anterior (A)
 *   +Z = Superior/Head (H), -Z = Inferior/Feet (F)
 */
function directionLabel(direction: vec3): DirectionLabel {
    const abs0 = Math.abs(direction[0]);
    const abs1 = Math.abs(direction[1]);
    const abs2 = Math.abs(direction[2]);

    if (abs0 >= abs1 && abs0 >= abs2) {
        return direction[0] > 0 ? 'L' : 'R';
    }
    if (abs1 >= abs0 && abs1 >= abs2) {
        return direction[1] > 0 ? 'P' : 'A';
    }
    return direction[2] > 0 ? 'H' : 'F';
}

/**
 * Compute the four directional labels [left, top, right, bottom]
 * for a viewport from the view's orientation matrix.
 *
 * The view orientation matrix columns represent view-X, view-Y,
 * view-Z axes in patient (DICOM LPS) space.
 *
 * Screen mapping:
 *   left edge  = -viewX direction in patient space
 *   right edge = +viewX direction in patient space
 *   top edge   = +viewY direction in patient space
 *   bottom edge = -viewY direction in patient space
 *
 * @returns [leftLabel, topLabel, rightLabel, bottomLabel]
 */
export function computeDirectionLabels(
    viewOrientation: ReadonlyMat3
): [DirectionLabel, DirectionLabel, DirectionLabel, DirectionLabel] {
    const viewX = vec3.fromValues(
        viewOrientation[0],
        viewOrientation[1],
        viewOrientation[2]
    );
    const viewY = vec3.fromValues(
        viewOrientation[3],
        viewOrientation[4],
        viewOrientation[5]
    );

    const negViewX = vec3.negate(vec3.create(), viewX);
    const negViewY = vec3.negate(vec3.create(), viewY);

    return [
        directionLabel(negViewX), // left edge
        directionLabel(viewY), // top edge
        directionLabel(viewX), // right edge
        directionLabel(negViewY), // bottom edge
    ];
}

/**
 * Standard DICOM patient positions for axis-aligned axial
 * acquisitions.
 */
export type PatientPosition = 'HFS' | 'HFP' | 'FFS' | 'FFP';

/**
 * Known orientation matrices for the four standard axial
 * patient positions (DICOM LPS convention).
 *
 *   HFS: row +X, col +Y → identity
 *   HFP: row -X, col -Y → normal +Z
 *   FFS: row +X, col -Y → normal -Z
 *   FFP: row -X, col +Y → normal -Z
 */
const PATIENT_POSITION_MAP: [PatientPosition, mat3][] = [
    ['HFS', mat3.fromValues(1, 0, 0, 0, 1, 0, 0, 0, 1)],
    ['HFP', mat3.fromValues(-1, 0, 0, 0, -1, 0, 0, 0, 1)],
    ['FFS', mat3.fromValues(1, 0, 0, 0, -1, 0, 0, 0, -1)],
    ['FFP', mat3.fromValues(-1, 0, 0, 0, 1, 0, 0, 0, -1)],
];

/**
 * Derive the DICOM PatientPosition tag value from a Grid's
 * orientation matrix. Returns the matching position string
 * for the four standard axial orientations (HFS, HFP, FFS,
 * FFP), or `null` for oblique/non-standard orientations.
 *
 * @param orientation 3x3 orientation matrix (from
 *   `Grid.getOrientation()` or DICOM ImageOrientationPatient).
 * @param tolerance Component-wise comparison tolerance.
 */
export function patientPositionFromOrientation(
    orientation: ReadonlyMat3,
    tolerance = 1e-4
): PatientPosition | null {
    for (const [position, expected] of PATIENT_POSITION_MAP) {
        let match = true;
        for (let i = 0; i < 9; i++) {
            if (Math.abs(orientation[i] - expected[i]) > tolerance) {
                match = false;
                break;
            }
        }
        if (match) {
            return position;
        }
    }
    return null;
}
